/**
 * Guardião da sessão — renova o JWT por TEMPO DECORRIDO, não pela hora do PC (GUPM-336).
 *
 * O auth-js agenda a renovação comparando `expires_at` com `Date.now()`. Num PC que
 * reseta a hora ao reiniciar (29/09/2026, posto do vigilante), ela simplesmente não
 * acontece e o painel trava a cada ~1h. Aqui `expires_in` é contado com timer: 55 min
 * são 55 min com qualquer relógio. `refreshSession()` vai direto ao `/token`, sem
 * consultar o relógio.
 *
 * Também mede o desvio do relógio pelo `iat` de cada token recém-emitido e o publica,
 * para a faixa que pede ao TI acertar o PC.
 *
 * Complementa o `fetch-com-renovacao` (que cobre o que o timer não pega: PC que
 * hibernou, aba congelada pelo navegador).
 */
import { desvioDoRelogioMs, iatDoToken, atrasoDaRenovacaoMs } from '@/domain/sessao/relogio';
import { encerrarSeRecusada } from '@/lib/renovar-sessao';

/** Recorte do `SupabaseAuthClient` que o guardião usa — injetável em teste. */
export interface AuthDoGuardiao {
  onAuthStateChange(
    cb: (evento: string, sessao: { access_token: string; expires_in: number } | null) => void,
  ): { data: { subscription: { unsubscribe(): void } } };
  refreshSession(): Promise<{ error: unknown }>;
  signOut(opcoes: { scope: 'local' }): Promise<unknown>;
}

/** Espera antes de tentar de novo quando a renovação falha (rede fora, servidor lento). */
const NOVA_TENTATIVA_MS = 30_000;

export function iniciarGuardiaoDaSessao(
  auth: AuthDoGuardiao,
  aoMedirDesvio: (desvioMs: number | null) => void,
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  /** Último access token visto; `null` = sem sessão. Ver o filtro de reemissão abaixo. */
  let ultimoToken: string | null = null;
  let parado = false;

  function cancelar(): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }

  function agendar(ms: number): void {
    cancelar();
    timer = setTimeout(renovar, ms);
  }

  async function renovar(): Promise<void> {
    timer = null;
    const { error } = await auth.refreshSession();
    // Sucesso chega como TOKEN_REFRESHED, que reagenda. Durante o `await` a sessão pode
    // ter acabado (SIGNED_OUT) ou o guardião ter sido parado: aí não há o que reagendar.
    if (!error || parado || ultimoToken === null || timer !== null) return;
    // Recusa do servidor: o auth-js com relógio atrasado preservaria a sessão morta.
    if ((await encerrarSeRecusada(auth, error)) === 'transitoria' && !parado && ultimoToken !== null) {
      agendar(NOVA_TENTATIVA_MS);
    }
  }

  const { data } = auth.onAuthStateChange((evento, sessao) => {
    if (evento === 'SIGNED_OUT' || !sessao) {
      cancelar();
      ultimoToken = null;
      aoMedirDesvio(null);
      return;
    }
    // O auth-js REEMITE `SIGNED_IN` com a sessão guardada sempre que a aba volta a ficar
    // visível (`_recoverAndRefresh`). Esse token não é recém-emitido: medir o desvio pelo
    // `iat` dele acusaria um relógio "adiantado" que não existe, e `expires_in` antigo
    // agendaria a renovação tarde demais. Só token novo conta.
    if (sessao.access_token === ultimoToken) return;
    ultimoToken = sessao.access_token;

    if (evento === 'INITIAL_SESSION') {
      // Sessão restaurada do storage: `expires_in` é o do login antigo e não diz quanto
      // falta. Renovar já dá um token com validade conhecida — 1 `/token` por abertura.
      agendar(0);
      return;
    }
    if (evento === 'SIGNED_IN' || evento === 'TOKEN_REFRESHED') {
      const iat = iatDoToken(sessao.access_token);
      if (iat !== null) aoMedirDesvio(desvioDoRelogioMs(iat, Date.now()));
      agendar(atrasoDaRenovacaoMs(sessao.expires_in));
    }
  });

  return () => {
    parado = true;
    cancelar();
    data.subscription.unsubscribe();
  };
}
